// frontend/src/components/Marquee/Marquee.js

import React, { useState, useRef, useEffect } from 'react';
import './Marquee.css';

const defaultItems = [
  'Admissions Open for 2026 — Apply Now',
  'PITAC Launches Advanced AI & Robotics Training Program',
  '58+ Technical Programs • 90+ Industry Partners',
  'PLC – HMI – SCADA Training Enrollment Closing Soon',
  '32,000+ Trained Students Since Inception',
];

const Marquee = ({ items = defaultItems, label = 'Latest' }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const barRef = useRef(null);
  const trackRef = useRef(null);
  const animationRef = useRef(null);
  const targetPositionRef = useRef(0);

  // Publish marquee height for App.css padding
  useEffect(() => {
    const el = barRef.current;
    const setHeightVar = () => {
      document.documentElement.style.setProperty(
        '--marquee-height',
        el ? `${el.offsetHeight}px` : '0px'
      );
    };
    
    setHeightVar();

    if (!el) return;
    
    const resizeObserver = new ResizeObserver(setHeightVar);
    resizeObserver.observe(el);
    
    return () => resizeObserver.disconnect();
  }, []);

  // Calculate and get item width with gap
  const getItemWidthWithGap = () => {
    if (trackRef.current && trackRef.current.children.length > 0) {
      const firstItem = trackRef.current.children[0];
      if (firstItem) {
        const style = window.getComputedStyle(firstItem);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;
        const marginLeft = parseFloat(style.marginLeft) || 0;
        const marginRight = parseFloat(style.marginRight) || 0;
        return firstItem.offsetWidth + paddingLeft + paddingRight + marginLeft + marginRight;
      }
    }
    return 300; // fallback
  };

  // Animation loop
  useEffect(() => {
    const animate = () => {
      // Pause if hovered OR manually paused
      if ((!isPaused && !isHovered) && !isTransitioning && trackRef.current) {
        setPosition((prev) => {
          const newPos = prev - 0.8; // Speed of scrolling
          const trackWidth = trackRef.current?.scrollWidth / 2 || 0;
          
          // Reset when we've scrolled through half the items
          if (Math.abs(newPos) >= trackWidth) {
            return 0;
          }
          return newPos;
        });
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPaused, isHovered, isTransitioning]);

  // Update track position with smooth transition
  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(${position}px)`;
    }
  }, [position]);

  // Handle forward navigation with smooth transition
  const handleForward = () => {
    if (isTransitioning || !trackRef.current) return;
    
    setIsTransitioning(true);
    
    const itemWidthWithGap = getItemWidthWithGap();
    const currentPosition = position;
    const targetPosition = currentPosition - itemWidthWithGap;
    const trackWidth = trackRef.current?.scrollWidth / 2 || 0;
    
    // Check if we need to wrap around
    let finalPosition = targetPosition;
    if (Math.abs(targetPosition) >= trackWidth) {
      finalPosition = 0;
    }
    
    // Store target for animation
    targetPositionRef.current = finalPosition;
    
    // Apply smooth transition
    if (trackRef.current) {
      trackRef.current.style.transition = 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
    }
    
    // Update position
    setPosition(finalPosition);
    
    // Reset transition after animation completes
    setTimeout(() => {
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
      }
      setIsTransitioning(false);
    }, 800);
  };

  // Handle rewind navigation with smooth transition
  const handleRewind = () => {
    if (isTransitioning || !trackRef.current) return;
    
    setIsTransitioning(true);
    
    const itemWidthWithGap = getItemWidthWithGap();
    const currentPosition = position;
    const targetPosition = currentPosition + itemWidthWithGap;
    const trackWidth = trackRef.current?.scrollWidth / 2 || 0;
    
    // Check if we need to wrap around
    let finalPosition = targetPosition;
    if (targetPosition > 0) {
      finalPosition = -trackWidth + itemWidthWithGap;
    }
    
    // Apply smooth transition
    if (trackRef.current) {
      trackRef.current.style.transition = 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)';
    }
    
    // Update position
    setPosition(finalPosition);
    
    // Reset transition after animation completes
    setTimeout(() => {
      if (trackRef.current) {
        trackRef.current.style.transition = 'none';
      }
      setIsTransitioning(false);
    }, 800);
  };

  // Toggle pause/play
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // Handle hover
  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Duplicate the list so the scrolling loop is seamless
  const trackItems = [...items, ...items];

  return (
    <div 
      className="marquee-bar" 
      role="region" 
      aria-label="Announcements" 
      ref={barRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="marquee-label">
        <span className="marquee-label-dot"></span>
        {label}
      </span>

      <div className="marquee-track-wrapper">
        <div className="marquee-track" ref={trackRef}>
          {trackItems.map((item, index) => (
            <span className="marquee-item" key={index}>
              {item}
              <span className="marquee-dot-sep">•</span>
            </span>
          ))}
        </div>
      </div>

      <div className="marquee-controls">
        <button
          type="button"
          className="marquee-btn marquee-btn-rewind"
          onClick={handleRewind}
          disabled={isTransitioning}
          aria-label="Rewind announcements"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="1 4 1 20 9 12 1 4"/>
            <polygon points="13 4 13 20 21 12 13 4"/>
          </svg>
        </button>
        <button
          type="button"
          className="marquee-btn marquee-btn-play"
          onClick={togglePause}
          aria-label={isPaused ? 'Play announcements' : 'Pause announcements'}
        >
          {isPaused ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="4" width="4" height="16"/>
              <rect x="14" y="4" width="4" height="16"/>
            </svg>
          )}
        </button>
        <button
          type="button"
          className="marquee-btn marquee-btn-forward"
          onClick={handleForward}
          disabled={isTransitioning}
          aria-label="Forward announcements"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 4 23 20 15 12 23 4"/>
            <polygon points="11 4 11 20 3 12 11 4"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Marquee;